import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import {
  Dropdown,
  FormItem,
  Modal,
  Typography,
} from '@getsynapse/design-system';
import { getForms, selectForms } from 'state/Forms/formSlice';
import { useImport } from 'Hooks/useImport';
import importIcon from 'assets/icons/import-icon.svg';
import { Form } from 'utils/customTypes';
import useImportRequests from '../../hooks/useImportRequests';

interface ImportRequestModalProps {
  showModal: boolean;
  onClose: () => void;
}

const ImportRequestModal: React.FC<ImportRequestModalProps> = ({
  showModal,
  onClose,
}) => {
  const dispatch = useDispatch();
  const requestForms = useSelector(selectForms);
  const [selectedForm, setSelectedForm] = useState<Form>();
  const [isLoading, setIsLoading] = useState(false);

  const onCloseModal = () => {
    setSelectedForm(undefined);
    onClose();
  };

  const { onData, currentUser, settings } = useImportRequests(selectedForm);
  const { startImport } = useImport(onData, currentUser, settings);

  const onStartImport = () => {
    setIsLoading(true);
    startImport();
    setTimeout(() => {
      setIsLoading(false);
      onCloseModal();
    }, 1500);
  };

  useEffect(() => {
    dispatch(
      getForms({
        organizationId: currentUser.organization_id!,
        published: true,
        isJsonSchema: false,
      })
    );
  }, [dispatch, currentUser.organization_id]);

  const selectForm: (formId: string) => void = useCallback(
    (formId) => {
      const selectedValue = requestForms.find(({ id }) => id === formId);
      if (selectedValue) {
        setSelectedForm(selectedValue);
      }
    },
    [requestForms]
  );

  return (
    <Modal
      title={intl.get('IMPORT_REQUEST_MODAL.TITLE')}
      titleIcon={{ src: importIcon }}
      isOpen={showModal}
      size='medium'
      closeModal={onCloseModal}
      aria-label={intl.get('IMPORT_REQUEST_MODAL.TITLE')}
      data-testid='import-request-select-form-modal'
      actionButtons={[
        {
          children: intl.get('IMPORT'),
          onClick: onStartImport,
          disabled: !selectedForm,
          loading: isLoading,
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: onCloseModal,
        },
      ]}
    >
      <Typography variant='body' className='mb-4 text-primary'>
        {intl.get('IMPORT_REQUEST_MODAL.DESCRIPTION')}
      </Typography>
      <FormItem
        label={intl.get('IMPORT_REQUEST_MODAL.LABEL')}
        className='w-full'
      >
        <Dropdown
          options={requestForms
            .slice()
            .sort((a, b) => a.title!.localeCompare(b.title!))}
          getOptionLabel={(option: Form) => option.title!}
          onChange={(option: Form) => selectForm(option.id!)}
          placeholder={intl.get('IMPORT_REQUEST_MODAL.SELECT_PLACEHOLDER')}
          triggerProps={{ 'data-testid': 'select-form' }}
        />
      </FormItem>
    </Modal>
  );
};

export default ImportRequestModal;
